import { useEffect, useState } from 'react';
import environment from '../environments/index';

declare global {
  interface Window {
    google: any;
  }
}

interface UseGoogleMapsReturn {
  isLoaded: boolean;
  loadError: string | null;
}

const SCRIPT_ATTR = 'data-edify-google-maps';

function isMapsReady(): boolean {
  return !!(window.google?.maps?.Map && window.google?.maps?.places);
}

export const useGoogleMaps = (): UseGoogleMapsReturn => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;

    if (isMapsReady()) {
      setIsLoaded(true);
      return () => {
        cancelled = true;
      };
    }

    const apiKey = environment.googleMaps.apiKey;
    if (!apiKey) {
      setLoadError('Google Maps API key no configurada');
      return () => {
        cancelled = true;
      };
    }

    const trySetReady = () => {
      if (cancelled) return;
      if (isMapsReady()) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        setLoadError(null);
        setIsLoaded(true);
      }
    };

    const startPolling = () => {
      trySetReady();
      if (isMapsReady() || intervalId) return;
      let attempts = 0;
      intervalId = setInterval(() => {
        attempts += 1;
        trySetReady();
        if (isMapsReady() && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        } else if (attempts >= 120) {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          if (!cancelled && !isMapsReady()) {
            setLoadError('Google Maps no se inicializó correctamente');
          }
        }
      }, 50);
    };

    let script = document.querySelector(`script[${SCRIPT_ATTR}]`) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.setAttribute(SCRIPT_ATTR, '1');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      script.addEventListener(
        'error',
        () => {
          if (!cancelled) {
            setLoadError('Error al cargar Google Maps API. Verifica la configuración de la API key.');
          }
        },
        { once: true }
      );
      script.addEventListener('load', startPolling, { once: true });
      document.head.appendChild(script);
      startPolling();
    } else {
      startPolling();
    }

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  return { isLoaded, loadError };
};
